import {
  restoreErrorMessage,
  useArchivedCanvases,
  useArchivedChats,
  useArchivedFiles,
  useArchivedSkills,
  useArchivedWorkspaces,
  useRecoverFileRecord,
  useRestoreCanvas,
  useRestoreFlowSession,
  useRestoreSkill,
  useRestoreWorkspace,
} from "@/api/archived";
import { SkillNameConflictDialog } from "@/components/SkillNameConflictDialog/SkillNameConflictDialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { WorkspacePageHeader } from "@/pages/workspaces/components/WorkspacePageHeader";
import { WorkspaceShell } from "@/pages/workspaces/components/WorkspaceShell";
import { cn } from "@/utils";
import { nameTakenSuggestion } from "@/utils/skillNameConflict";
import { FileRecordTypeIcon, Spinner } from "@cohesive/ui-components";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { AxiosError } from "axios";
import { differenceInCalendarDays, formatDistanceToNow } from "date-fns";
import {
  Archive,
  Frame,
  Layers,
  Lock,
  MessagesSquare,
  RotateCcw,
  ScrollText,
} from "lucide-react";
import { ReactNode, useState } from "react";
import toast from "react-hot-toast";

/** Restorable window for soft-deleted S3 files; mirrors the backend's 14-day query window. */
const FILE_RESTORE_WINDOW_DAYS = 14;

function archivedAgo(ts?: string): string {
  if (!ts) return "Archived";
  return `Archived ${formatDistanceToNow(new Date(ts), { addSuffix: true })}`;
}

function fileDaysLeft(deletedAt?: string): string {
  if (!deletedAt) return "";
  const daysGone = differenceInCalendarDays(new Date(), new Date(deletedAt));
  const left = Math.max(FILE_RESTORE_WINDOW_DAYS - daysGone, 0);
  return left === 1 ? "1 day left" : `${left} days left`;
}

/**
 * Underline tab trigger per the design-system tab spec: neutral text on a
 * hairline baseline, 2px foreground underline for the active state, color-only
 * hover. Colors/focus use the same n-scale + rust ring as the rest of the app.
 */
function TrashTabTrigger({
  value,
  label,
  count,
  hasMore,
}: {
  value: string;
  label: string;
  count?: number;
  /** True when more pages exist beyond the loaded count. */
  hasMore?: boolean;
}) {
  return (
    <TabsPrimitive.Trigger
      value={value}
      className={cn(
        "relative -mb-px inline-flex h-9 cursor-pointer items-center gap-1.5 border-b-2 border-transparent px-3",
        "text-sm font-medium text-n-60 outline-none transition-colors",
        "hover:text-n-90",
        "data-[state=active]:border-n-90 data-[state=active]:text-n-90",
        "focus-visible:outline-2 focus-visible:outline-rust focus-visible:-outline-offset-2",
      )}
    >
      {label}
      {count !== undefined && count > 0 && (
        <span className="text-xs font-normal text-n-50 tabular-nums">
          {count}
          {hasMore ? "+" : ""}
        </span>
      )}
    </TabsPrimitive.Trigger>
  );
}

function LoadMoreRow({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  if (!hasNextPage) return null;
  return (
    <div className="flex justify-center pt-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={onLoadMore}
        disabled={isFetchingNextPage}
        className="text-muted-foreground hover:text-foreground"
      >
        {isFetchingNextPage ? "Loading…" : "Load more"}
      </Button>
    </div>
  );
}

function TrashRow({
  icon,
  title,
  meta,
  onRestore,
  isRestoring,
  restoreBlockedReason,
}: {
  icon: ReactNode;
  title: string;
  meta: string;
  onRestore: () => void;
  isRestoring: boolean;
  /** When set, the Restore button is replaced with a locked state badge. */
  restoreBlockedReason?: string;
}) {
  return (
    <TableRow className="group hover:bg-n-10">
      <TableCell className="min-w-[280px] px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-4 shrink-0 items-center justify-center text-n-50">
            {icon}
          </span>
          <span className="truncate text-sm text-foreground">{title}</span>
        </div>
      </TableCell>
      <TableCell className="px-3 py-2.5 text-xs text-muted-foreground">
        {meta}
      </TableCell>
      <TableCell className="px-3 py-2.5 text-right">
        {restoreBlockedReason ? (
          // Blocked: always-visible read-only hint — no disabled button so
          // users aren't misled by a greyed-out affordance.
          <span
            className="inline-flex shrink-0 items-center gap-1 px-2 text-xs text-n-50"
            title={restoreBlockedReason}
          >
            <Lock className="h-3 w-3" />
            {restoreBlockedReason}
          </span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRestore}
            disabled={isRestoring}
            className={cn(
              "shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100",
              isRestoring && "opacity-100",
            )}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            {isRestoring ? "Restoring…" : "Restore"}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * Table shell shared by every tab — same column rhythm as the legacy
 * workspace list views (44%-ish name column with a floor, metadata column,
 * fixed trailing actions column), with the Table primitive's hairline row
 * separators.
 */
function TrashTable({ children }: { children: ReactNode }) {
  return (
    <Table>
      <colgroup>
        <col className="w-[60%]" />
        <col className="w-[40%]" />
        <col className="w-[140px]" />
      </colgroup>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="h-10 min-w-[280px] px-3 text-left align-middle text-xs font-medium text-muted-foreground">
            Name
          </TableHead>
          <TableHead className="h-10 px-3 text-left align-middle text-xs font-medium text-muted-foreground">
            Archived
          </TableHead>
          <TableHead className="h-10 px-3" />
        </TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  );
}

function TrashEmptyState({ description }: { description: string }) {
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center">
      <Archive className="h-10 w-10 text-muted-foreground/20" />
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function TabLoading() {
  return (
    <div className="flex items-center justify-center min-h-[320px]">
      <Spinner size="lg" />
    </div>
  );
}

export function TrashPage() {
  const workspacesQuery = useArchivedWorkspaces();
  const canvasesQuery = useArchivedCanvases();
  const chatsQuery = useArchivedChats();
  const filesQuery = useArchivedFiles();
  const skillsQuery = useArchivedSkills();

  const restoreWorkspace = useRestoreWorkspace({
    onSuccess: () => toast.success("Restored Workspace."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Workspace")),
  });
  // 409s carry "restore the parent first" guidance — restores never cascade
  // upward, so a canvas/chat whose parent is also trashed points the user at
  // the parent's restore instead.
  const restoreCanvas = useRestoreCanvas({
    onSuccess: () => toast.success("Restored Canvas."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Canvas")),
  });
  const restoreChat = useRestoreFlowSession({
    onSuccess: () => toast.success("Restored chat."),
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore chat")),
  });
  const recoverFile = useRecoverFileRecord({
    onSuccess: () => toast.success("Restored file."),
    onError: () => toast.error("Failed to restore file"),
  });
  const [skillConflict, setSkillConflict] = useState<{
    skillId: string;
    originalName: string;
    suggestedName: string;
  } | null>(null);
  const restoreSkill = useRestoreSkill({
    onSuccess: () => {
      setSkillConflict(null);
      toast.success("Restored skill.");
    },
    onError: (err: AxiosError, variables) => {
      // A name collision (the trashed name was reused) → open the "Keep both"
      // dialog. Detected from the typed error or the raw 409 body, so it works
      // regardless of how the error was wrapped before reaching here. Use the
      // mutation's own variables (not `restoringId` state, which can lag or hold
      // a different row's id under rapid/concurrent restores) so the dialog
      // always targets the skill that actually failed.
      const suggestedName = nameTakenSuggestion(err);
      if (suggestedName !== null) {
        // Use the trashed skill's real name for display rather than deriving it
        // from the suggestion (which is fragile for names ending in -<digits>).
        const originalName =
          skills.find((sk) => sk.id === variables.skillId)?.name ??
          suggestedName.replace(/-\d+$/, "");
        setSkillConflict({
          skillId: variables.skillId,
          originalName,
          suggestedName,
        });
        return;
      }
      toast.error(restoreErrorMessage(err, "Failed to restore skill"));
    },
  });
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const workspaces =
    workspacesQuery.data?.pages.flatMap((p) => p.data.workspaces) ?? [];
  const canvases =
    canvasesQuery.data?.pages.flatMap((p) => p.data.canvases) ?? [];
  const chats =
    chatsQuery.data?.pages.flatMap((p) => p.data.flow_sessions) ?? [];
  const files =
    filesQuery.data?.pages.flatMap((p) => p.data.file_records) ?? [];
  const skills = skillsQuery.data?.pages.flatMap((p) => p.data.skills) ?? [];

  return (
    <>
      <SkillNameConflictDialog
        open={skillConflict !== null}
        originalName={skillConflict?.originalName ?? ""}
        suggestedName={skillConflict?.suggestedName ?? ""}
        isPending={restoreSkill.isPending}
        onCancel={() => setSkillConflict(null)}
        onKeepBoth={() => {
          if (!skillConflict) return;
          restoreSkill.mutate({
            skillId: skillConflict.skillId,
            newName: skillConflict.suggestedName,
          });
        }}
      />
      <title>Archived</title>
      <WorkspaceShell>
        <WorkspacePageHeader
          breadcrumbs={[{ label: "Home", to: "/" }, { label: "Archived" }]}
        />

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 md:px-8 lg:px-10 pt-2 pb-12">
            <TabsPrimitive.Root defaultValue="workspaces">
              <TabsPrimitive.List className="flex items-center gap-1 border-b border-border/60">
                <TrashTabTrigger
                  value="workspaces"
                  label="Workspaces"
                  count={workspaces.length}
                  hasMore={workspacesQuery.hasNextPage}
                />
                <TrashTabTrigger
                  value="canvases"
                  label="Canvases"
                  count={canvases.length}
                  hasMore={canvasesQuery.hasNextPage}
                />
                <TrashTabTrigger
                  value="chats"
                  label="Chats"
                  count={chats.length}
                  hasMore={chatsQuery.hasNextPage}
                />
                <TrashTabTrigger
                  value="files"
                  label="Files"
                  count={files.length}
                  hasMore={filesQuery.hasNextPage}
                />
                <TrashTabTrigger
                  value="skills"
                  label="Skills"
                  count={skills.length}
                  hasMore={skillsQuery.hasNextPage}
                />
              </TabsPrimitive.List>

              <TabsPrimitive.Content
                value="workspaces"
                className="pt-4 outline-none"
              >
                {workspacesQuery.isLoading ? (
                  <TabLoading />
                ) : workspaces.length === 0 ? (
                  <TrashEmptyState description="Archived Workspaces appear here." />
                ) : (
                  <>
                    <TrashTable>
                      {workspaces.map((ws) => (
                        <TrashRow
                          key={ws.id}
                          icon={<Layers className="h-4 w-4" />}
                          title={ws.name || "Untitled"}
                          meta={archivedAgo(ws.archived_at)}
                          isRestoring={
                            restoreWorkspace.isPending && restoringId === ws.id
                          }
                          onRestore={() => {
                            setRestoringId(ws.id);
                            restoreWorkspace.mutate(ws.id);
                          }}
                        />
                      ))}
                    </TrashTable>
                    <LoadMoreRow
                      hasNextPage={workspacesQuery.hasNextPage}
                      isFetchingNextPage={workspacesQuery.isFetchingNextPage}
                      onLoadMore={() => workspacesQuery.fetchNextPage()}
                    />
                  </>
                )}
              </TabsPrimitive.Content>

              <TabsPrimitive.Content
                value="canvases"
                className="pt-4 outline-none"
              >
                {canvasesQuery.isLoading ? (
                  <TabLoading />
                ) : canvases.length === 0 ? (
                  <TrashEmptyState description="Archived Canvases appear here. Canvases archived with a Workspace restore with it." />
                ) : (
                  <>
                    <TrashTable>
                      {canvases.map((canvas) => (
                        <TrashRow
                          key={canvas.id}
                          icon={<Frame className="h-4 w-4" />}
                          title={canvas.name || "Untitled"}
                          meta={archivedAgo(canvas.archived_at)}
                          restoreBlockedReason={
                            canvas.parent_archived
                              ? "Restore unavailable"
                              : undefined
                          }
                          isRestoring={
                            restoreCanvas.isPending && restoringId === canvas.id
                          }
                          onRestore={() => {
                            setRestoringId(canvas.id);
                            restoreCanvas.mutate(canvas.id);
                          }}
                        />
                      ))}
                    </TrashTable>
                    <LoadMoreRow
                      hasNextPage={canvasesQuery.hasNextPage}
                      isFetchingNextPage={canvasesQuery.isFetchingNextPage}
                      onLoadMore={() => canvasesQuery.fetchNextPage()}
                    />
                  </>
                )}
              </TabsPrimitive.Content>

              <TabsPrimitive.Content
                value="chats"
                className="pt-4 outline-none"
              >
                {chatsQuery.isLoading ? (
                  <TabLoading />
                ) : chats.length === 0 ? (
                  <TrashEmptyState description="Archived chats appear here. Chats archived with a Canvas or Workspace restore with it." />
                ) : (
                  <>
                    <TrashTable>
                      {chats.map((chat) => (
                        <TrashRow
                          key={chat.id}
                          icon={<MessagesSquare className="h-4 w-4" />}
                          title={chat.name || "Untitled chat"}
                          meta={archivedAgo(chat.archived_at)}
                          restoreBlockedReason={
                            chat.parent_archived
                              ? "Restore unavailable"
                              : undefined
                          }
                          isRestoring={
                            restoreChat.isPending && restoringId === chat.id
                          }
                          onRestore={() => {
                            setRestoringId(chat.id);
                            restoreChat.mutate(chat.id);
                          }}
                        />
                      ))}
                    </TrashTable>
                    <LoadMoreRow
                      hasNextPage={chatsQuery.hasNextPage}
                      isFetchingNextPage={chatsQuery.isFetchingNextPage}
                      onLoadMore={() => chatsQuery.fetchNextPage()}
                    />
                  </>
                )}
              </TabsPrimitive.Content>

              <TabsPrimitive.Content
                value="files"
                className="pt-4 outline-none"
              >
                {filesQuery.isLoading ? (
                  <TabLoading />
                ) : files.length === 0 ? (
                  <TrashEmptyState description="Archived files appear here and stay restorable for 14 days." />
                ) : (
                  <>
                    <TrashTable>
                      {files.map((file) => (
                        <TrashRow
                          key={file.id}
                          icon={
                            <FileRecordTypeIcon
                              contentType={file.content_type}
                              originType={file.origin_type}
                              className="h-4 w-4"
                            />
                          }
                          title={
                            file.path?.split("/").filter(Boolean).pop() ||
                            file.path
                          }
                          meta={[
                            archivedAgo(
                              file.last_modified_at ?? file.updated_at,
                            ),
                            fileDaysLeft(
                              file.last_modified_at ?? file.updated_at,
                            ),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          isRestoring={
                            recoverFile.isPending && restoringId === file.id
                          }
                          onRestore={() => {
                            setRestoringId(file.id);
                            recoverFile.mutate(file.id);
                          }}
                        />
                      ))}
                    </TrashTable>
                    <LoadMoreRow
                      hasNextPage={filesQuery.hasNextPage}
                      isFetchingNextPage={filesQuery.isFetchingNextPage}
                      onLoadMore={() => filesQuery.fetchNextPage()}
                    />
                  </>
                )}
              </TabsPrimitive.Content>

              <TabsPrimitive.Content
                value="skills"
                className="pt-4 outline-none"
              >
                {skillsQuery.isLoading ? (
                  <TabLoading />
                ) : skills.length === 0 ? (
                  <TrashEmptyState description="Skills you archive appear here." />
                ) : (
                  <>
                    <TrashTable>
                      {skills.map((skill) => (
                        <TrashRow
                          key={skill.id}
                          icon={<ScrollText className="h-4 w-4" />}
                          title={skill.name || "Untitled"}
                          meta={archivedAgo(skill.archived_at)}
                          isRestoring={
                            restoreSkill.isPending && restoringId === skill.id
                          }
                          onRestore={() => {
                            setRestoringId(skill.id);
                            restoreSkill.mutate({ skillId: skill.id });
                          }}
                        />
                      ))}
                    </TrashTable>
                    <LoadMoreRow
                      hasNextPage={skillsQuery.hasNextPage}
                      isFetchingNextPage={skillsQuery.isFetchingNextPage}
                      onLoadMore={() => skillsQuery.fetchNextPage()}
                    />
                  </>
                )}
              </TabsPrimitive.Content>
            </TabsPrimitive.Root>
          </div>
        </div>
      </WorkspaceShell>
    </>
  );
}
